import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { equipmentKeys, jobKeys } from '@/lib/queryKeys';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut, operationsClient } from '@/lib/api';
import type {
  Equipment,
  EquipmentCategory,
  EquipmentConditionUpdateInput,
  EquipmentFilters,
  EquipmentInventoryReport,
  EquipmentListResponse,
  EquipmentSettings,
  EquipmentUploadUrlResponse,
  EquipmentWarrantyInput,
  CreateEquipmentInput,
  ConfirmJobEquipmentInstallationsInput,
  CreateEquipmentCategoryInput,
  EquipmentWorkHistory,
  EstimateEquipmentResponse,
  JobEquipmentResponse,
  MaintenanceOverdueResponse,
  ReplaceEquipmentAssociationsInput,
  ReplacementPipelineReport,
  UpdateEquipmentInput,
  WarrantyExpiringResponse,
  WarrantyReport,
} from '@/types/equipment';

// ─── Query-param serialization ────────────────────────────────────────────────

function buildEquipmentParams(filters: EquipmentFilters = {}): Record<string, string> {
  const params: Record<string, string> = {};
  const set = (key: string, value: string | number | undefined | null) => {
    if (value === undefined || value === null || value === '') return;
    params[key] = String(value);
  };
  set('customerId', filters.customerId);
  set('serviceAddressId', filters.serviceAddressId);
  set('category', filters.category);
  set('status', filters.status);
  set('condition', filters.condition);
  set('warrantyStatus', filters.warrantyStatus);
  set('systemGroupId', filters.systemGroupId);
  set('minAge', filters.minAge);
  set('maxAge', filters.maxAge);
  set('q', filters.q);
  set('limit', filters.limit);
  set('offset', filters.offset);
  set('cursor', filters.cursor);
  if (filters.tags && filters.tags.length > 0) params.tags = filters.tags.join(',');
  return params;
}

// ─── Reads ──────────────────────────────────────────────────────────────────

export function useEquipment(filters: EquipmentFilters = {}, enabled = true) {
  return useApiQuery<EquipmentListResponse>({
    queryKey: equipmentKeys.list(filters),
    queryFn: ({ signal }) =>
      apiGet<EquipmentListResponse>(operationsClient, '/equipment', {
        params: buildEquipmentParams(filters),
        signal,
      }),
    enabled,
    showErrorToast: false,
  });
}

/**
 * Membership sale pricing must see the complete installed-equipment set. A
 * normal inventory list is intentionally paged; reusing its first page can
 * underquote extra units and freeze an incomplete sold-home promise.
 */
export async function fetchAllEquipment(
  filters: EquipmentFilters,
  signal?: AbortSignal,
): Promise<EquipmentListResponse> {
  const pageSize = 200;
  const itemsById = new Map<string, Equipment>();
  let cursor: string | undefined;
  let total = 0;
  for (;;) {
    const page = await apiGet<EquipmentListResponse>(operationsClient, '/equipment', {
      params: buildEquipmentParams({
        ...filters,
        limit: pageSize,
        offset: undefined,
        cursor,
      }),
      signal,
    });
    total = page.total;
    page.items.forEach((equipment) => itemsById.set(equipment.id, equipment));
    if (!page.nextCursor || page.items.length === 0) break;
    cursor = page.nextCursor;
  }
  return { items: [...itemsById.values()], total };
}

export function useAllEquipment(filters: EquipmentFilters = {}, enabled = true) {
  return useApiQuery<EquipmentListResponse>({
    queryKey: [...equipmentKeys.list(filters), 'all-pages'],
    queryFn: ({ signal }) => fetchAllEquipment(filters, signal),
    enabled,
    showErrorToast: false,
  });
}

/** A single contact's equipment — the contact-detail Equipment tab. */
export function useContactEquipment(customerId: string) {
  return useApiQuery<EquipmentListResponse>({
    queryKey: equipmentKeys.byCustomer(customerId),
    queryFn: ({ signal }) =>
      apiGet<EquipmentListResponse>(operationsClient, '/equipment', {
        params: buildEquipmentParams({ customerId }),
        signal,
      }),
    enabled: Boolean(customerId),
    showErrorToast: false,
  });
}

export function useEquipmentItem(id: string | undefined) {
  return useApiQuery<Equipment>({
    queryKey: equipmentKeys.detail(id ?? ''),
    queryFn: ({ signal }) => apiGet<Equipment>(operationsClient, `/equipment/${id}`, { signal }),
    enabled: Boolean(id),
  });
}

export function useEquipmentWorkHistory(id: string | undefined) {
  return useApiQuery<EquipmentWorkHistory>({
    queryKey: equipmentKeys.workHistory(id ?? ''),
    queryFn: ({ signal }) =>
      apiGet<EquipmentWorkHistory>(operationsClient, `/equipment/${id}/work-history`, { signal }),
    enabled: Boolean(id),
  });
}

export function useJobEquipment(jobId: string | undefined) {
  return useApiQuery<JobEquipmentResponse>({
    queryKey: jobKeys.equipment(jobId ?? ''),
    queryFn: ({ signal }) =>
      apiGet<JobEquipmentResponse>(operationsClient, `/jobs/${jobId}/equipment`, { signal }),
    enabled: Boolean(jobId),
  });
}

export function useEstimateEquipment(estimateId: string | undefined) {
  return useApiQuery<EstimateEquipmentResponse>({
    queryKey: [...equipmentKeys.all(), 'estimate', estimateId ?? ''] as const,
    queryFn: ({ signal }) =>
      apiGet<EstimateEquipmentResponse>(operationsClient, `/estimates/${estimateId}/equipment`, {
        signal,
      }),
    enabled: Boolean(estimateId),
  });
}

export function useEquipmentCategories() {
  return useApiQuery<EquipmentCategory[]>({
    queryKey: equipmentKeys.categories(),
    queryFn: ({ signal }) =>
      apiGet<EquipmentCategory[]>(operationsClient, '/equipment-categories', { signal }),
    staleTime: 5 * 60_000,
  });
}

export function useEquipmentSettings() {
  return useApiQuery<EquipmentSettings>({
    queryKey: equipmentKeys.settings(),
    queryFn: ({ signal }) =>
      apiGet<EquipmentSettings>(operationsClient, '/equipment-settings', { signal }),
    staleTime: 5 * 60_000,
  });
}

// ─── Office dashboard reads ───────────────────────────────────────────────────

export function useEquipmentInventoryReport(enabled = true) {
  return useApiQuery<EquipmentInventoryReport>({
    queryKey: equipmentKeys.report('inventory'),
    queryFn: ({ signal }) =>
      apiGet<EquipmentInventoryReport>(operationsClient, '/reports/equipment/inventory', {
        signal,
      }),
    enabled,
    showErrorToast: false,
  });
}

export function useEquipmentWarrantyReport(enabled = true) {
  return useApiQuery<WarrantyReport>({
    queryKey: equipmentKeys.report('warranty'),
    queryFn: ({ signal }) =>
      apiGet<WarrantyReport>(operationsClient, '/reports/equipment/warranty', { signal }),
    enabled,
    showErrorToast: false,
  });
}

export function useReplacementPipelineReport(enabled = true) {
  return useApiQuery<ReplacementPipelineReport>({
    queryKey: equipmentKeys.report('replacement-pipeline'),
    queryFn: ({ signal }) =>
      apiGet<ReplacementPipelineReport>(
        operationsClient,
        '/reports/equipment/replacement-pipeline',
        {
          signal,
        },
      ),
    enabled,
    showErrorToast: false,
  });
}

export function useWarrantyExpiring(days?: number, enabled = true) {
  return useApiQuery<WarrantyExpiringResponse>({
    queryKey: equipmentKeys.warrantyExpiring(days),
    queryFn: ({ signal }) =>
      apiGet<WarrantyExpiringResponse>(operationsClient, '/equipment/warranty-expiring', {
        params: days ? { days: String(days) } : undefined,
        signal,
      }),
    enabled,
    showErrorToast: false,
  });
}

export function useMaintenanceOverdue(enabled = true) {
  return useApiQuery<MaintenanceOverdueResponse>({
    queryKey: equipmentKeys.maintenanceOverdue(),
    queryFn: ({ signal }) =>
      apiGet<MaintenanceOverdueResponse>(operationsClient, '/equipment/maintenance-overdue', {
        signal,
      }),
    enabled,
    showErrorToast: false,
  });
}

// ─── Invalidation helper ──────────────────────────────────────────────────────

export function invalidateEquipmentQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  equipment: Pick<Equipment, 'id' | 'customerId'>,
  previousCustomerId?: string,
) {
  void queryClient.invalidateQueries({ queryKey: equipmentKeys.detail(equipment.id) });
  void queryClient.invalidateQueries({ queryKey: equipmentKeys.lists() });
  void queryClient.invalidateQueries({ queryKey: equipmentKeys.reports() });
  // These dashboard outreach queries live outside the `reports()` key family.
  // Equipment create/update can change both immediately, so refresh them too.
  void queryClient.invalidateQueries({
    queryKey: equipmentKeys.warrantyExpiringAll(),
  });
  void queryClient.invalidateQueries({
    queryKey: equipmentKeys.maintenanceOverdue(),
  });
  if (previousCustomerId && previousCustomerId !== equipment.customerId) {
    void queryClient.invalidateQueries({ queryKey: equipmentKeys.byCustomer(previousCustomerId) });
  }
  if (equipment.customerId) {
    void queryClient.invalidateQueries({
      queryKey: equipmentKeys.byCustomer(equipment.customerId),
    });
  }
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export function useCreateEquipment() {
  const queryClient = useQueryClient();
  return useApiMutation<Equipment, CreateEquipmentInput>({
    mutationFn: (dto) =>
      apiPost<Equipment, CreateEquipmentInput>(operationsClient, '/equipment', dto),
    onSuccess: (equipment) => {
      invalidateEquipmentQueries(queryClient, equipment);
      toast.success('Equipment added');
    },
  });
}

export function useUpdateEquipment() {
  const queryClient = useQueryClient();
  return useApiMutation<Equipment, { id: string; input: UpdateEquipmentInput; previousCustomerId?: string }>({
    mutationFn: ({ id, input }) =>
      apiPut<Equipment, UpdateEquipmentInput>(operationsClient, `/equipment/${id}`, input),
    onSuccess: (equipment, variables) => {
      invalidateEquipmentQueries(queryClient, equipment, variables.previousCustomerId);
      toast.success('Equipment updated');
    },
  });
}

export function useDecommissionEquipment() {
  const queryClient = useQueryClient();
  return useApiMutation<Equipment, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) =>
      apiPost<Equipment, { reason: string }>(operationsClient, `/equipment/${id}/decommission`, {
        reason,
      }),
    onSuccess: (equipment) => {
      invalidateEquipmentQueries(queryClient, equipment);
      toast.success('Equipment decommissioned');
    },
  });
}

export function useReplaceEquipment() {
  const queryClient = useQueryClient();
  return useApiMutation<
    Equipment & { systemMismatch?: boolean },
    { id: string; replacementEquipmentId: string }
  >({
    mutationFn: ({ id, replacementEquipmentId }) =>
      apiPost<Equipment & { systemMismatch?: boolean }, { replacementEquipmentId: string }>(
        operationsClient,
        `/equipment/${id}/replace`,
        { replacementEquipmentId },
      ),
    onSuccess: (equipment) => {
      invalidateEquipmentQueries(queryClient, equipment);
      toast.success('Equipment marked as replaced');
    },
  });
}

export function useUpdateEquipmentCondition() {
  const queryClient = useQueryClient();
  return useApiMutation<Equipment, { id: string; input: EquipmentConditionUpdateInput }>({
    mutationFn: ({ id, input }) =>
      apiPut<Equipment, EquipmentConditionUpdateInput>(
        operationsClient,
        `/equipment/${id}/condition`,
        input,
      ),
    onSuccess: (equipment) => {
      invalidateEquipmentQueries(queryClient, equipment);
      toast.success('Condition updated');
    },
  });
}

export function useUpdateEquipmentWarranty() {
  const queryClient = useQueryClient();
  return useApiMutation<Equipment, { id: string; warranty: EquipmentWarrantyInput }>({
    mutationFn: ({ id, warranty }) =>
      apiPut<Equipment, EquipmentWarrantyInput>(
        operationsClient,
        `/equipment/${id}/warranty`,
        warranty,
      ),
    onSuccess: (equipment) => {
      invalidateEquipmentQueries(queryClient, equipment);
      toast.success('Warranty updated');
    },
  });
}

export function useReplaceJobEquipment(jobId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<JobEquipmentResponse, ReplaceEquipmentAssociationsInput>({
    mutationFn: (input) =>
      apiPut<JobEquipmentResponse, ReplaceEquipmentAssociationsInput>(
        operationsClient,
        `/jobs/${jobId}/equipment`,
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobKeys.equipment(jobId) });
      void queryClient.invalidateQueries({ queryKey: equipmentKeys.all() });
      toast.success('Job equipment updated');
    },
  });
}

export function useConfirmJobEquipmentInstallations(jobId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<JobEquipmentResponse, ConfirmJobEquipmentInstallationsInput>({
    mutationFn: (input) =>
      apiPost<JobEquipmentResponse, ConfirmJobEquipmentInstallationsInput>(
        operationsClient,
        `/jobs/${jobId}/equipment/installations`,
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobKeys.equipment(jobId) });
      void queryClient.invalidateQueries({ queryKey: equipmentKeys.all() });
      toast.success('Installed equipment confirmed');
    },
  });
}

// ─── Photos & documents (presigned-upload flow) ───────────────────────────────

/**
 * Uploads a file for an equipment: request a presigned URL, PUT the bytes straight
 * to S3 (a bare axios call — no auth interceptors/baseURL), then register the
 * resulting key. `kind` selects the `/photos` or `/documents` endpoints.
 */
export function useUploadEquipmentMedia(equipmentId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<
    Equipment,
    { file: File; kind: 'photos' | 'documents'; type?: string; caption?: string; name?: string }
  >({
    mutationFn: async ({ file, kind, type, caption, name }) => {
      const { uploadUrl, fileKey } = await apiPost<
        EquipmentUploadUrlResponse,
        { fileName: string; contentType: string }
      >(operationsClient, `/equipment/${equipmentId}/${kind}/upload-url`, {
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
      });

      await axios.put(uploadUrl, file, {
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });

      if (kind === 'photos') {
        return apiPost<Equipment, { fileKey: string; type?: string; caption?: string }>(
          operationsClient,
          `/equipment/${equipmentId}/photos`,
          { fileKey, type, caption },
        );
      }
      return apiPost<Equipment, { fileKey: string; name: string; type?: string }>(
        operationsClient,
        `/equipment/${equipmentId}/documents`,
        { fileKey, name: name ?? file.name, type },
      );
    },
    onSuccess: (equipment) => {
      invalidateEquipmentQueries(queryClient, equipment);
    },
  });
}

export function useDeleteEquipmentMedia(equipmentId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<Equipment, { kind: 'photos' | 'documents'; index: number }>({
    mutationFn: ({ kind, index }) =>
      apiDelete<Equipment>(operationsClient, `/equipment/${equipmentId}/${kind}/${index}`),
    onSuccess: (equipment) => {
      invalidateEquipmentQueries(queryClient, equipment);
      toast.success('Removed');
    },
  });
}

// ─── Categories (global taxonomy + per-tenant enablement) ─────────────────────

/** A global category flagged with whether this tenant has enabled it. */
export type AvailableEquipmentCategory = EquipmentCategory & {
  enabled: boolean;
};

/** The whole taxonomy with enablement flags — powers the "add category" picker. */
export function useAvailableEquipmentCategories(enabled = true) {
  return useApiQuery<AvailableEquipmentCategory[]>({
    queryKey: equipmentKeys.categoriesAvailable(),
    queryFn: ({ signal }) =>
      apiGet<AvailableEquipmentCategory[]>(operationsClient, '/equipment-categories/available', {
        signal,
      }),
    staleTime: 5 * 60_000,
    enabled,
  });
}

function invalidateCategories(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: equipmentKeys.categories() });
  void queryClient.invalidateQueries({
    queryKey: equipmentKeys.categoriesAvailable(),
  });
}

/** Create a tenant-owned category. The server enables it in the same write. */
export function useCreateEquipmentCategory() {
  const queryClient = useQueryClient();
  return useApiMutation<EquipmentCategory, CreateEquipmentCategoryInput>({
    mutationFn: (input) =>
      apiPost<EquipmentCategory, CreateEquipmentCategoryInput>(
        operationsClient,
        '/equipment-categories',
        input,
      ),
    onSuccess: () => {
      invalidateCategories(queryClient);
      toast.success('Custom category created');
    },
  });
}

/** Enable a global category for the tenant (add it to their list). */
export function useEnableEquipmentCategory() {
  const queryClient = useQueryClient();
  return useApiMutation<EquipmentCategory, string>({
    mutationFn: (categoryId) =>
      apiPost<EquipmentCategory, undefined>(
        operationsClient,
        `/equipment-categories/${categoryId}/enable`,
        undefined,
      ),
    onSuccess: () => {
      invalidateCategories(queryClient);
      toast.success('Category added');
    },
  });
}

/** Enable a checked set in one request, with one cache refresh and toast. */
export function useEnableEquipmentCategories() {
  const queryClient = useQueryClient();
  return useApiMutation<EquipmentCategory[], string[]>({
    mutationFn: (categoryIds) =>
      apiPost<EquipmentCategory[], { categoryIds: string[] }>(
        operationsClient,
        '/equipment-categories/enable',
        { categoryIds },
      ),
    onSuccess: (categories) => {
      invalidateCategories(queryClient);
      toast.success(`${categories.length} categor${categories.length === 1 ? 'y' : 'ies'} added`);
    },
  });
}

/** Remove a category from the tenant (blocked server-side while in use). */
export function useDisableEquipmentCategory() {
  const queryClient = useQueryClient();
  return useApiMutation<{ disabled: boolean }, string>({
    mutationFn: (categoryId) =>
      apiDelete<{ disabled: boolean }>(
        operationsClient,
        `/equipment-categories/${categoryId}/enable`,
      ),
    onSuccess: () => {
      invalidateCategories(queryClient);
      toast.success('Category removed');
    },
  });
}

export function useUpdateEquipmentSettings() {
  const queryClient = useQueryClient();
  return useApiMutation<EquipmentSettings, Partial<EquipmentSettings>>({
    mutationFn: (dto) =>
      apiPatch<EquipmentSettings, Partial<EquipmentSettings>>(
        operationsClient,
        '/equipment-settings',
        dto,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: equipmentKeys.settings() });
      toast.success('Settings saved');
    },
  });
}
